/* eslint-disable no-else-return */
import React, { Fragment } from 'react';
import * as Yup from 'yup';
import { withFormik, Field } from 'formik';
import { filter, sortBy, map } from 'lodash';
import { Form, Button, Container, Icon, Header } from 'semantic-ui-react';
import { withLocalize } from 'react-localize-redux';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import {
  HHUrl,
  HHFileUploader,
  HHTinyMCE,
  HHCheckboxGroup,
  HHInput
} from './fields';
import { FORM_NAME } from '../forms/formConstant';
import { githubRegex, urlRegex, ipRegex } from './regex';

const formikEnhancer = withFormik({
  validationSchema: ({ translate }) =>
    Yup.object().shape({
      fileName: Yup.string(),
      name: Yup.string()
        .required(translate('form.error.mixed.required'))
        .max(60, translate('form.error.array.max')),
      preview: Yup.string().url(translate('form.error.string.url')),
      fileUrl: Yup.string()
        .url(translate('form.error.string.url'))
        .test(
          'submitAtLeastOne',
          translate('form.error.mixed.submitAtLeastOne'),
          // eslint-disable-next-line func-names
          function (value) {
            return (
              !!value ||
              !!this.parent.githubUrl ||
              !!this.parent.mediaUrl ||
              !!this.parent.websiteUrl
            );
          }
        ),
      websiteUrl: Yup.string() // .url(translate('form.error.string.url'))
        .test(
          'validUrl',
          translate('form.error.string.url'),
          // eslint-disable-next-line func-names, prefer-arrow-callback
          function (value) {
            return !value || urlRegex.test(value) || ipRegex.test(value);
          }
        ),
      githubUrl: Yup.string().matches(githubRegex, {
        excludeEmptyString: true,
        message: translate('form.error.regex.githubUrl')
      }),
      mediaUrl: Yup.string() // .url(translate('form.error.string.url')),
        .test(
          'validUrl',
          translate('form.error.string.url'),
          // eslint-disable-next-line func-names, prefer-arrow-callback
          function (value) {
            return !value || urlRegex.test(value) || ipRegex.test(value);
          }
        ),
      description: Yup.string(),
      isPrivate: Yup.boolean(),
      awardsEntered: Yup.array().of(Yup.number()),
      topicsEntered: Yup.array().of(Yup.number())
    }),
  mapPropsToValues: props => {
    const { oldSubmission } = props;
    if (oldSubmission) {
      const {
        fileName,
        name,
        preview,
        fileUrl,
        awardEntered,
        topicEntered,
        description,
        websiteUrl,
        githubUrl,
        mediaUrl,
        isPrivate
      } = oldSubmission;

      const oldAwardsEntered = awardEntered
        ? awardEntered.map(award => award.id)
        : [];
      const newAwardsEntered = filter(props.event.awardSet, {
        default: true
      }).map(award => award.id);
      const oldTopicsEntered = topicEntered
        ? topicEntered.map(topic => topic.id)
        : [];
      const newTopicsEntered = filter(props.event.topicSet, {
        default: true
      }).map(topic => topic.id);

      return {
        // initial values
        fileName: fileName || '',
        name: name || '',
        preview: preview || '',
        fileUrl: fileUrl || '',
        websiteUrl: websiteUrl || '',
        githubUrl: githubUrl || '',
        mediaUrl: mediaUrl || '',
        description: description || '',
        isPrivate: isPrivate || false,
        awardsEntered:
          oldAwardsEntered === newAwardsEntered
            ? oldAwardsEntered
            : newAwardsEntered,
        topicsEntered:
          oldTopicsEntered === newTopicsEntered
            ? oldTopicsEntered
            : newTopicsEntered
      };
    } else {
      return {
        // initial values
        fileName: `${props.team.name} `,
        name: '',
        preview: '',
        fileUrl: '',
        websiteUrl: '',
        githubUrl: '',
        mediaUrl: '',
        description: '',
        isPrivate: false,
        awardsEntered: filter(props.event.awardSet, { default: true }).map(
          award => award.id
        ),
        topicsEntered: filter(props.event.topicSet, { default: true }).map(
          topic => topic.id
        )
      };
    }
  },
  handleSubmit: (values, { props, setSubmitting }) => {
    // call parent handle submit function
    props.handleSubmitProject(values).then(() => {
      setSubmitting(false);
    });
  },
  displayName: FORM_NAME.SUBMIT_PROJECT_FORM
});

const SubmitProjectForm = props => {
  const {
    event,
    values,
    handleSubmit,
    isSubmitting,
    translate,
    errors
    // user
  } = props;

  let error = '';
  if (Object.keys(errors).length) {
    error = errors[Object.keys(errors)[0]];
  }

  return (
    <Fragment>
      <div className='reactModal_header'>
        <h1 className='reactModal_header_title'>
          {translate('submitMyProject')}
        </h1>
      </div>
      <div className='reactModal_content'>
        <Container>
          <Form onSubmit={handleSubmit} className='submitProjectForm'>
            <Form.Field>
              <label htmlFor='preview'>{translate('projectThumbnail')}</label>
              <Field
                name='preview'
                component={HHFileUploader}
                otherProps={{
                  id: 'preview',
                  type: 'image_16x9',
                  trigger: (
                    <Button
                      primary basic type='button'
                      color='blue'
                    >
                      {translate('uploadThumbnail')}
                    </Button>
                  )
                }}
              />
              {values.preview && (
                <img
                  src={values.preview}
                  alt='Project Cover Background'
                  className='mb1 upload-img-edit'
                />
              )}
            </Form.Field>

            <Form.Field>
              <label>{translate('projectNameRequired')}</label>
              <Field
                name='name'
                component={HHInput}
                otherProps={{
                  id: 'name',
                  placeholder: translate('projectName'),
                  type: 'text'
                }}
              />
            </Form.Field>

            <p className='aside-text'>
              {translate('text.submitOneOfFollowing')}
            </p>
            <Form.Field>
              <label htmlFor='websitelink'>{translate('websiteLink')}</label>
              <Field
                name='websiteUrl'
                component={HHUrl}
                otherProps={{
                  id: 'websitelink',
                  icon: 'linkify',
                  placeholder: 'event.hackhub.com',
                  type: 'text'
                }}
              />
            </Form.Field>

            <Form.Field>
              <label htmlFor='videolink'>{translate('videoLink')}</label>
              <Field
                name='mediaUrl'
                component={HHUrl}
                otherProps={{
                  id: 'videolink',
                  icon: 'video',
                  placeholder: 'youtu.be/xxxxxx',
                  type: 'text'
                }}
              />
            </Form.Field>

            <Form.Field>
              <label htmlFor='githublink'>{translate('gitHubLink')}</label>
              <Field
                name='githubUrl'
                component={HHUrl}
                otherProps={{
                  id: 'githublink',
                  icon: 'github',
                  placeholder: 'github.com/xxx/xxx',
                  type: 'text'
                }}
              />
            </Form.Field>

            <Form.Field>
              <label>
                {translate('Upload Your File')} (zip, pdf or ppt file)
              </label>
              <Field
                name='fileUrl'
                component={HHFileUploader}
                otherProps={{
                  type: 'compression + document',
                  trigger: (
                    <Button type='button' basic color='blue'>
                      <i className='fal fa-arrow-from-bottom fa-mr' />
                      {translate('upload.file')}
                    </Button>
                  )
                }}
              />

              {values.fileUrl && (
                <Button
                  type='a'
                  href={values.fileUrl}
                  basic
                  color='blue'
                  target='_blank'
                >
                  <i className='fal fa-arrow-to-bottom fa-mr' />
                  {translate('Download')}
                </Button>
              )}
            </Form.Field>

            <Form.Field>
              <label>{translate('Description')}</label>
              <Field
                name='description'
                placeholder="Please Give a brief description of your project. If applicable, don't forget to mention the technology stack you used for your project as well as the business model you used"
                component={HHTinyMCE}
              />
            </Form.Field>

            <Form.Field className='awardsField'>
              <Header as='h2' size='medium'>
                <Icon name='pencil' />
                {translate('text.selectTopicQuestion')}
              </Header>
              <Field
                name='topicsEntered'
                component={HHCheckboxGroup}
                items={map(sortBy(event.topicSet, ['ordering']), topic => {
                  const newTopic = { ...topic, disabled: !!topic.default };
                  return newTopic;
                })}
              />
            </Form.Field>

            <Form.Field className='awardsField'>
              <Header as='h2' size='medium'>
                <Icon name='trophy' />
                {translate('text.selectAwardQuestion')}
              </Header>
              <Field
                name='awardsEntered'
                component={HHCheckboxGroup}
                items={map(sortBy(event.awardSet, ['ordering']), awd => {
                  const newAwd = { ...awd, disabled: !!awd.default };
                  return newAwd;
                })}
              />
            </Form.Field>

            <div className='g-form-footer'>
              <p className='msg-latestSubmission'>
                {translate(
                  'Your latest submission will be considered as the final version.'
                )}
              </p>
              {error ? (
                <p className='msg-latestSubmission' style={{ color: 'red' }}>
                  {error}
                </p>
              ) : (
                ''
              )}

              <Button
                loading={isSubmitting}
                primary
                type='submit'
                size='large'
                disabled={isSubmitting}
                className='g-modal-btn-primary g-rounded'
              >
                {translate('Submit Now')}
              </Button>
            </div>
          </Form>
        </Container>
      </div>
    </Fragment>
  );
};

export default compose(
  withLocalize,
  withRouter,
  formikEnhancer
)(SubmitProjectForm);



// WEBPACK FOOTER //
// ./src/components/forms/submitProjectForm.js